// Where the TV is in the song, kept in the browser so a reload picks up from there instead
// of starting the song over. A reload happens when a site admin asks for one or when the
// server is updated with a new web app (utils/SiteVersion.ts), and with this the room hears
// a few seconds of silence rather than the song from the top.
//
// Only the same song resumes, and only if it was saved in the last few minutes: a TV
// switched off overnight starts its song from the top in the morning, as it always has.

export interface SavedPlayback {
  videoId: number;
  seconds: number;
}

const KEY_PREFIX = 'jookable.playback.';
const FRESH_FOR_MS = 5 * 60 * 1000;
const SAVE_EVERY_MS = 2000;

const keyFor = (playerIdentifier: string) => `${KEY_PREFIX}${(playerIdentifier || '').toLowerCase()}`;

export const readSavedPlayback = (playerIdentifier: string, now: number = Date.now()): SavedPlayback | null => {
  try {
    const raw = window.localStorage.getItem(keyFor(playerIdentifier));
    const saved = raw ? JSON.parse(raw) : null;
    if (
      saved &&
      typeof saved.videoId === 'number' &&
      typeof saved.seconds === 'number' &&
      typeof saved.at === 'number' &&
      now - saved.at < FRESH_FOR_MS
    ) {
      return { videoId: saved.videoId, seconds: saved.seconds };
    }
  } catch (e) {
    // Storage blocked or junk in it: start the song from the top, as before.
  }
  return null;
};

let lastSaved = 0;

/** Called on every timeupdate; writes at most every two seconds. */
export const savePlayback = (playerIdentifier: string, videoId: number, seconds: number, now: number = Date.now()) => {
  if (now - lastSaved < SAVE_EVERY_MS || !playerIdentifier || !(seconds >= 0)) {
    return;
  }
  lastSaved = now;
  try {
    window.localStorage.setItem(keyFor(playerIdentifier), JSON.stringify({ videoId, seconds, at: now }));
  } catch (e) {
    // Full or blocked storage only costs the resume.
  }
};
